import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Toast, Popup, Picker } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PinyinMatch from 'pinyin-match'
import { getMyClasses, getAllClasses, getClassStudents, getCurrentAcademicYear, getAcademicYears, searchStudents as searchStudentsApi } from '../../api/academic'
import { getPhysicalTests, savePhysicalTest } from '../../api/physicalTest'
import { isAdmin, isStaff, canManagePhysicalTest } from '../../utils/permissions'
import SportScoreKeyboard from '../../components/SportScoreKeyboard'
import {
  TEST_ITEMS,
  getStandardGradeName,
  checkStudentScorePass,
  parseTimeToSeconds
} from '../../utils/standardsScorer'

const GRADE_FILTER_OPTIONS = [
  '全部', '一年级', '二年级', '三年级', '四年级', '五年级', '六年级', '七年级', '八年级', '九年级'
]

export default function SingleRecord() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialClassId = searchParams.get('classId')
  const initialStudentId = searchParams.get('studentId')

  const { userInfo } = useSelector(state => state.user)
  const hasAccess = isAdmin(userInfo) || isStaff(userInfo) || canManagePhysicalTest(userInfo)

  // 学年与班级
  const [academicYears, setAcademicYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [showYearPicker, setShowYearPicker] = useState(false)

  const [allClasses, setAllClasses] = useState([])
  const [allRosterStudents, setAllRosterStudents] = useState([])
  const [gradeFilter, setGradeFilter] = useState('全部')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)

  // 单人录入模式状态
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [studentCohortList, setStudentCohortList] = useState([])
  const [studentScores, setStudentScores] = useState({})
  const [existingRecord, setExistingRecord] = useState(null)
  const [saving, setSaving] = useState(false)

  // 虚拟小键盘控制
  const [activeItemKey, setActiveItemKey] = useState(null)
  const [showKeyboard, setShowKeyboard] = useState(false)

  // 1. 初始化数据
  useEffect(() => {
    const initData = async () => {
      setLoading(true)
      try {
        const [currRes, yearsRes, classesRes, studentsRes] = await Promise.allSettled([
          getCurrentAcademicYear(),
          getAcademicYears(),
          getMyClasses({ page_size: 300 }),
          searchStudentsApi({ page_size: 1000 }),
        ])

        let yList = []
        if (yearsRes.status === 'fulfilled' && yearsRes.value) {
          const val = yearsRes.value
          if (Array.isArray(val)) yList = val
          else if (val.options) yList = val.options.map(o => String(o.value || o))
          else if (val.years) yList = val.years
        }
        if (!yList.length) {
          const now = new Date()
          const yr = now.getFullYear()
          yList = [now.getMonth() >= 7 ? `${yr}-${yr + 1}` : `${yr - 1}-${yr}`]
        }
        setAcademicYears(yList)

        let currYear = localStorage.getItem('current_academic_year')
        if (!currYear || !yList.includes(currYear)) {
          if (currRes.status === 'fulfilled' && currRes.value?.current_academic_year) {
            currYear = currRes.value.current_academic_year
          } else {
            currYear = yList[0]
          }
        }
        setSelectedYear(currYear)

        let cItems = []
        if (classesRes.status === 'fulfilled' && classesRes.value) {
          cItems = classesRes.value.items || classesRes.value.data || []
        }
        if (!cItems.length) {
          const fallback = await getAllClasses({ page_size: 300 })
          cItems = fallback?.items || fallback?.data || []
        }
        setAllClasses(cItems)

        let stuList = []
        if (studentsRes.status === 'fulfilled' && studentsRes.value) {
          stuList = studentsRes.value.items || studentsRes.value.data || []
        }
        setAllRosterStudents(stuList)

        // 如果 URL 传了初始学生 ID，直接进入该学生的录入态
        if (initialStudentId) {
          const foundStu = stuList.find(s => String(s.id) === String(initialStudentId))
          if (foundStu) {
            handleSelectStudentForEntry(foundStu, stuList)
          }
        }
      } catch (err) {
        console.error('初始化单人速测失败:', err)
      } finally {
        setLoading(false)
      }
    }
    initData()
  }, [initialStudentId])

  // 2. 匹配搜索学生列表 (支持汉字、拼音首字母简拼、学号、教育ID)
  const matchedStudents = useMemo(() => {
    if (!searchQuery.trim() || !allRosterStudents.length) return []
    const q = searchQuery.trim()
    return allRosterStudents.filter(stu => {
      const name = stu.real_name || stu.name || stu.student_name || ''
      const no = String(stu.student_no || '')
      const eduId = String(stu.education_id || '')
      const clsName = String(stu.class_name || '')

      if (name.includes(q) || no.includes(q) || eduId.includes(q) || clsName.includes(q)) return true
      try {
        if (PinyinMatch.match(name, q)) return true
      } catch {
        // ignore
      }
      return false
    }).slice(0, 40)
  }, [allRosterStudents, searchQuery])

  // 3. 按年级筛选的班级列表
  const filteredClasses = useMemo(() => {
    if (gradeFilter === '全部') return allClasses
    return allClasses.filter(c => {
      const g = c.grade || ''
      return g.includes(gradeFilter)
    })
  }, [allClasses, gradeFilter])

  // 4. 进入学生的体测成绩录入
  const handleSelectStudentForEntry = async (student, cohort = []) => {
    setSelectedStudent(student)
    setStudentCohortList(cohort.length > 0 ? cohort : allRosterStudents)
    setShowKeyboard(false)
    setActiveItemKey(null)

    // 加载该学生当前学年的已有成绩
    try {
      const res = await getPhysicalTests({
        student_id: student.id,
        academic_year: selectedYear,
        page_size: 10,
      })
      const records = res?.items || res?.data || (Array.isArray(res) ? res : [])
      const latest = records[0] || null
      setExistingRecord(latest)

      // 解析已有成绩到表单
      const initScores = {
        height: latest?.height !== undefined && latest?.height !== null ? String(latest.height) : '',
        weight: latest?.weight !== undefined && latest?.weight !== null ? String(latest.weight) : '',
        vital_capacity: latest?.vital_capacity !== undefined && latest?.vital_capacity !== null ? String(latest.vital_capacity) : '',
        run_50m: latest?.run_50m !== undefined && latest?.run_50m !== null ? String(latest.run_50m) : '',
        sit_and_reach: latest?.sit_and_reach !== undefined && latest?.sit_and_reach !== null ? String(latest.sit_and_reach) : '',
        skip_rope: latest?.skip_rope !== undefined && latest?.skip_rope !== null ? String(latest.skip_rope) : '',
        sit_up: latest?.sit_up !== undefined && latest?.sit_up !== null ? String(latest.sit_up) : '',
        standing_long_jump: latest?.standing_long_jump !== undefined && latest?.standing_long_jump !== null ? String(latest.standing_long_jump) : '',
        run_50m_8x: latest?.run_50m_8x !== undefined && latest?.run_50m_8x !== null ? String(latest.run_50m_8x) : '',
        run_800m: latest?.run_800m !== undefined && latest?.run_800m !== null ? String(latest.run_800m) : '',
        run_1000m: latest?.run_1000m !== undefined && latest?.run_1000m !== null ? String(latest.run_1000m) : '',
        pull_up: latest?.pull_up !== undefined && latest?.pull_up !== null ? String(latest.pull_up) : '',
      }
      setStudentScores(initScores)
    } catch (err) {
      console.warn('获取学生已有成绩失败:', err)
      setExistingRecord(null)
      setStudentScores({})
    }
  }

  // 5. 点击班级卡片：展开班级学生
  const handleSelectClass = async (cls) => {
    try {
      Toast.loading({ message: '加载班级学生...', duration: 0 })
      const res = await getClassStudents(cls.id)
      Toast.clear()
      const list = res?.items || res?.students || (Array.isArray(res) ? res : [])
      if (list.length > 0) {
        handleSelectStudentForEntry(list[0], list)
      } else {
        Toast.info('该班级暂无学生名册')
      }
    } catch (err) {
      Toast.clear()
      Toast.fail('加载班级学生失败')
    }
  }

  // 当前学生适用的体测项目
  const standardGrade = useMemo(() => {
    if (!selectedStudent) return '小学一年级'
    return getStandardGradeName(selectedStudent.grade || selectedStudent.class_name || '小学一年级')
  }, [selectedStudent])

  const applicableItems = useMemo(() => {
    if (!selectedStudent) return []
    const grade = standardGrade
    const gender = selectedStudent.gender === 'female' || selectedStudent.gender === '女' ? '女' : '男'

    const items = [
      { key: 'height', label: '身高', unit: 'cm', category: 'body', field: 'height' },
      { key: 'weight', label: '体重', unit: 'kg', category: 'body', field: 'weight' },
      { key: 'vital_capacity', label: '肺活量', unit: 'ml', category: 'body', field: 'vital_capacity' },
      { key: 'run_50m', label: '50米跑', unit: '秒', category: 'fitness', field: 'run_50m' },
      { key: 'sit_and_reach', label: '坐位体前屈', unit: 'cm', category: 'fitness', field: 'sit_and_reach' },
      { key: 'skip_rope', label: '1分钟跳绳', unit: '次', category: 'fitness', field: 'skip_rope' },
    ]

    // 三年级及以上必测 1分钟仰卧起坐
    if (!grade.includes('一年级') && !grade.includes('二年级')) {
      items.push({ key: 'sit_up', label: '1分钟仰卧起坐', unit: '次', category: 'fitness', field: 'sit_up' })
    }

    // 五、六年级测 50米×8往返跑
    if (grade.includes('五年级') || grade.includes('六年级')) {
      items.push({ key: 'run_50m_8x', label: '50米×8往返跑', unit: '分·秒', category: 'fitness', field: 'run_50m_8x' })
    }

    // 初高中项目
    if (grade.includes('初中') || grade.includes('高中')) {
      items.push({ key: 'standing_long_jump', label: '立定跳远', unit: 'cm', category: 'fitness', field: 'standing_long_jump' })
      if (gender === '男') {
        items.push({ key: 'run_1000m', label: '1000米跑', unit: '分·秒', category: 'fitness', field: 'run_1000m' })
        items.push({ key: 'pull_up', label: '引体向上', unit: '次', category: 'fitness', field: 'pull_up' })
      } else {
        items.push({ key: 'run_800m', label: '800米跑', unit: '分·秒', category: 'fitness', field: 'run_800m' })
      }
    }

    return items
  }, [selectedStudent, standardGrade])

  // BMI 计算
  const bmiInfo = useMemo(() => {
    const h = parseFloat(studentScores.height)
    const w = parseFloat(studentScores.weight)
    if (!h || !w || h <= 0 || w <= 0) return { bmi: '--', score: null, gradeText: '--', status: 'untested' }
    const res = checkStudentScorePass(selectedStudent, { isCompound: true, key: 'height_weight' }, { height: h, weight: w }, standardGrade)
    return {
      bmi: res.studentValue || '--',
      score: res.score,
      gradeText: res.gradeText,
      status: res.status,
      scoreColor: res.scoreColor,
      gradeBg: res.gradeBg,
      gradeBorder: res.gradeBorder,
    }
  }, [studentScores.height, studentScores.weight, selectedStudent, standardGrade])

  // 实时单项成绩与综合得分估算
  const overallEvaluation = useMemo(() => {
    if (!selectedStudent) return { standardScore: 0, bonus: 0, total: 0, level: '待测' }

    let totalScore = 0
    let count = 0

    // 统计 BMI
    if (bmiInfo.score !== null) {
      totalScore += bmiInfo.score * 0.15 // BMI 权重 15%
      count++
    }

    // 统计其余各项
    applicableItems.forEach(item => {
      if (item.key === 'height' || item.key === 'weight') return
      const val = studentScores[item.key]
      if (val !== undefined && val !== null && val !== '') {
        const res = checkStudentScorePass(selectedStudent, item, val, standardGrade)
        if (res.tested && res.score !== null) {
          totalScore += res.score * 0.15
          count++
        }
      }
    })

    const finalScore = count > 0 ? Math.round(totalScore / (count * 0.15)) : 0
    let level = '不及格'
    if (finalScore >= 90) level = '优秀'
    else if (finalScore >= 80) level = '良好'
    else if (finalScore >= 60) level = '及格'

    return {
      standardScore: finalScore,
      bonus: 0,
      total: finalScore,
      level: count > 0 ? level : '待测',
    }
  }, [selectedStudent, applicableItems, studentScores, standardGrade, bmiInfo])

  // 当前学生在群体名册中的索引与上/下一位切换
  const currentStudentIndex = useMemo(() => {
    if (!selectedStudent || !studentCohortList.length) return -1
    return studentCohortList.findIndex(s => s.id === selectedStudent.id)
  }, [selectedStudent, studentCohortList])

  const handlePrevStudent = () => {
    if (currentStudentIndex > 0) {
      handleSelectStudentForEntry(studentCohortList[currentStudentIndex - 1], studentCohortList)
    }
  }

  const handleNextStudent = () => {
    if (currentStudentIndex < studentCohortList.length - 1) {
      handleSelectStudentForEntry(studentCohortList[currentStudentIndex + 1], studentCohortList)
    } else {
      Toast.info('已到达最后一名学生')
    }
  }

  // 点击成绩项唤起虚拟小键盘
  const handleOpenKeypad = (itemKey) => {
    setActiveItemKey(itemKey)
    setShowKeyboard(true)
  }

  // 键盘数值变更
  const handleScoreChange = (newVal) => {
    if (!activeItemKey) return
    setStudentScores(prev => ({
      ...prev,
      [activeItemKey]: newVal,
    }))
  }

  // 键盘下一项 / 上一项
  const currentItemIndex = useMemo(() => {
    if (!activeItemKey) return -1
    return applicableItems.findIndex(it => it.key === activeItemKey)
  }, [activeItemKey, applicableItems])

  const handleNextField = () => {
    if (currentItemIndex < applicableItems.length - 1) {
      setActiveItemKey(applicableItems[currentItemIndex + 1].key)
    } else {
      setShowKeyboard(false)
      Toast.info('本学生所有项目已录入完成')
    }
  }

  const handlePrevField = () => {
    if (currentItemIndex > 0) {
      setActiveItemKey(applicableItems[currentItemIndex - 1].key)
    }
  }

  // 保存成绩
  const handleSaveStudentTest = async (autoNext = false) => {
    if (!selectedStudent || saving) return
    setSaving(true)
    try {
      const payload = {
        student_id: selectedStudent.id,
        class_id: selectedStudent.class_id,
        academic_year: selectedYear,
        test_date: new Date().toISOString().split('T')[0],
      }

      applicableItems.forEach(item => {
        const val = studentScores[item.key]
        if (val !== undefined && val !== null && val !== '') {
          if (val === '缺考' || val === '免考' || val === '缓考') {
            if (val === '缺考') {
              payload[item.key] = 0
            }
          } else {
            const isLongRun = ['run_800m', 'run_1000m', 'run_50m_8x'].includes(item.key)
            if (isLongRun || item.unit === '分·秒') {
              payload[item.key] = parseTimeToSeconds(val, isLongRun)
            } else {
              const parsed = parseFloat(val)
              if (!isNaN(parsed)) {
                payload[item.key] = parsed
              }
            }
          }
        }
      })

      await savePhysicalTest(payload)
      Toast.success('成绩已成功保存！')
      setShowKeyboard(false)

      if (autoNext && currentStudentIndex < studentCohortList.length - 1) {
        handleNextStudent()
      }
    } catch (err) {
      console.error('保存单人速测成绩失败:', err)
      Toast.fail(err.response?.data?.detail || '保存失败，请检查网络')
    } finally {
      setSaving(false)
    }
  }

  // 当前键盘展示项信息
  const activeItemObj = useMemo(() => {
    return applicableItems.find(it => it.key === activeItemKey) || applicableItems[0]
  }, [activeItemKey, applicableItems])

  // ==================== 视图 2：单人全项极速录入与报告态 ====================
  if (selectedStudent) {
    const isMale = selectedStudent.gender === 'male' || selectedStudent.gender === '男'

    return (
      <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] pb-32 font-sans select-none">
        {/* 顶栏导航 */}
        <div
          className="sticky top-0 z-30 px-4 pt-3 pb-3.5 shadow-sm"
          style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)' }}
        >
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSelectedStudent(null)}
              className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>

            <div className="text-center">
              <h1 className="text-[17.5px] font-bold text-white tracking-wide leading-tight">
                {selectedStudent.real_name || selectedStudent.name}的体测录入
              </h1>
              <p className="text-[11.5px] text-white/80 font-normal mt-0.5">
                单人速测 · 精准单兵补测与成长档案
              </p>
            </div>

            {/* 上一位 / 下一位快捷切换 */}
            <div className="flex items-center gap-1">
              <button
                disabled={currentStudentIndex <= 0}
                onClick={handlePrevStudent}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                  currentStudentIndex > 0 ? 'bg-white/20 active:bg-white/30' : 'bg-white/5 opacity-40'
                }`}
                title="上一位学生"
              >
                ◀
              </button>
              <button
                disabled={currentStudentIndex >= studentCohortList.length - 1}
                onClick={handleNextStudent}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                  currentStudentIndex < studentCohortList.length - 1 ? 'bg-white/20 active:bg-white/30' : 'bg-white/5 opacity-40'
                }`}
                title="下一位学生"
              >
                ▶
              </button>
            </div>
          </div>
        </div>

        <div className="px-3.5 pt-3 space-y-3.5 max-w-lg mx-auto">
          {/* 1. 学生档案基本信息卡 */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-extrabold text-[19px] shadow-sm ${
                  isMale ? 'bg-[#056b4f]' : 'bg-[#ec4899]'
                }`}
              >
                {(selectedStudent.real_name || selectedStudent.name || '?').charAt(0)}
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[17px] font-extrabold text-[#0f172a]">
                    {selectedStudent.real_name || selectedStudent.name}
                  </span>
                  <span
                    className={`px-1.5 py-0.2 rounded text-[11px] font-bold ${
                      isMale ? 'bg-[#e8f6f0] text-[#056b4f]' : 'bg-[#fce7f3] text-[#ec4899]'
                    }`}
                  >
                    {isMale ? '男' : '女'}
                  </span>
                  <span className="text-[11px] text-[#64748b]">
                    {currentStudentIndex >= 0 ? `${currentStudentIndex + 1} / ${studentCohortList.length}` : ''}
                  </span>
                </div>

                <div className="text-[12px] text-[#64748b] mt-0.5">
                  {selectedStudent.grade || ''} · {selectedStudent.class_name || '主校区'}
                  {selectedStudent.education_id ? ` · ID: ${selectedStudent.education_id}` : ''}
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate(`/physical-test/portfolio?studentId=${selectedStudent.id}`)}
              className="px-3 py-1.5 rounded-xl bg-[#ecfdf5] border border-[#a7f3d0] text-[#00b578] text-[12px] font-bold active:bg-[#d1fae5] transition-all"
            >
              成长档案 ↗
            </button>
          </div>

          {/* 2. 综合测试评定看板 (与 App 评定卡完全对齐) */}
          <div className="rounded-2xl p-4 shadow-sm border border-[#a7f3d0] bg-gradient-to-br from-[#f0fdf4] to-[#ecfdf5]">
            <div className="flex items-center justify-between pb-2 border-b border-[#a7f3d0]">
              <span className="text-[14px] font-bold text-[#065f46]">综合测试评定</span>
              <span className="px-2.5 py-0.5 rounded-full text-[12px] font-extrabold bg-[#059669] text-white">
                {overallEvaluation.level}
              </span>
            </div>

            <div className="flex items-center justify-between pt-3">
              <div>
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-[11px] text-[#475569]">标准分</span>
                    <div className="text-[18px] font-extrabold text-[#0f172a]">
                      {overallEvaluation.standardScore}
                    </div>
                  </div>
                  <div>
                    <span className="text-[11px] text-[#475569]">加分</span>
                    <div className="text-[18px] font-extrabold text-[#f97316]">
                      +{overallEvaluation.bonus}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-[#64748b] mt-1">
                  综合得分 = 标准分 + 加分 (上限 120 分)
                </div>
              </div>

              <div className="text-right">
                <span className="text-[11px] text-[#475569]">综合得分</span>
                <div className="text-[32px] font-black text-[#059669] leading-none">
                  {overallEvaluation.total}
                </div>
              </div>
            </div>
          </div>

          {/* 3. 身体形态与机能 (身高、体重、BMI、肺活量) */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0] space-y-2.5">
            <div className="flex items-center gap-2 pb-1 border-b border-[#f1f5f9]">
              <span className="w-1.5 h-4 bg-[#056b4f] rounded-full"></span>
              <span className="text-[14px] font-bold text-[#0f172a]">身体形态与机能</span>
              <span className="text-[11px] text-[#94a3b8]">4 项</span>
            </div>

            {/* 身高行 */}
            <div
              onClick={() => handleOpenKeypad('height')}
              className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                activeItemKey === 'height' ? 'bg-[#f0fdf4] border-[#056b4f] ring-2 ring-[#056b4f]/20' : 'bg-[#f8fafc] border-[#e2e8f0]'
              }`}
            >
              <span className="text-[13.5px] font-bold text-[#0f172a]">身高</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[16px] font-extrabold text-[#0f172a]">
                  {studentScores.height || '--'}
                </span>
                <span className="text-[12px] text-[#94a3b8]">cm</span>
                {activeItemKey === 'height' && <span className="w-0.5 h-4 bg-[#056b4f] animate-pulse"></span>}
              </div>
            </div>

            {/* 体重行 */}
            <div
              onClick={() => handleOpenKeypad('weight')}
              className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                activeItemKey === 'weight' ? 'bg-[#f0fdf4] border-[#056b4f] ring-2 ring-[#056b4f]/20' : 'bg-[#f8fafc] border-[#e2e8f0]'
              }`}
            >
              <span className="text-[13.5px] font-bold text-[#0f172a]">体重</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[16px] font-extrabold text-[#0f172a]">
                  {studentScores.weight || '--'}
                </span>
                <span className="text-[12px] text-[#94a3b8]">kg</span>
                {activeItemKey === 'weight' && <span className="w-0.5 h-4 bg-[#056b4f] animate-pulse"></span>}
              </div>
            </div>

            {/* 体重指数 BMI (自动联动计算与国标定级) */}
            <div className="p-3 rounded-xl bg-[#f0fdf4] border border-[#a7f3d0] flex items-center justify-between">
              <div>
                <span className="text-[13.5px] font-bold text-[#065f46]">体重指数 BMI</span>
                <div className="text-[10px] text-[#64748b]">按标准自动由身高体重换算</div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[16px] font-extrabold text-[#065f46]">{bmiInfo.bmi}</span>
                <span
                  className="px-2 py-0.5 rounded text-[11px] font-bold border"
                  style={{
                    backgroundColor: bmiInfo.gradeBg || '#ecfdf5',
                    borderColor: bmiInfo.gradeBorder || '#a7f3d0',
                    color: bmiInfo.scoreColor || '#059669',
                  }}
                >
                  {bmiInfo.gradeText}
                </span>
              </div>
            </div>

            {/* 肺活量 */}
            {applicableItems.find(it => it.key === 'vital_capacity') && (
              <div
                onClick={() => handleOpenKeypad('vital_capacity')}
                className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                  activeItemKey === 'vital_capacity' ? 'bg-[#f0fdf4] border-[#056b4f] ring-2 ring-[#056b4f]/20' : 'bg-[#f8fafc] border-[#e2e8f0]'
                }`}
              >
                <span className="text-[13.5px] font-bold text-[#0f172a]">肺活量</span>
                <div className="flex items-center gap-2">
                  <span className="text-[16px] font-extrabold text-[#0f172a]">
                    {studentScores.vital_capacity || '--'}
                  </span>
                  <span className="text-[12px] text-[#94a3b8]">ml</span>
                  {activeItemKey === 'vital_capacity' && <span className="w-0.5 h-4 bg-[#056b4f] animate-pulse"></span>}
                  {studentScores.vital_capacity && (
                    <span
                      className="px-2 py-0.5 rounded text-[11px] font-bold border"
                      style={(() => {
                        const r = checkStudentScorePass(selectedStudent, { key: 'vital_capacity' }, studentScores.vital_capacity, standardGrade)
                        return { backgroundColor: r.gradeBg, borderColor: r.gradeBorder, color: r.scoreColor }
                      })()}
                    >
                      {checkStudentScorePass(selectedStudent, { key: 'vital_capacity' }, studentScores.vital_capacity, standardGrade).gradeText}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 4. 身体素质项目列表 (动态匹配年级) */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0] space-y-2.5">
            <div className="flex items-center gap-2 pb-1 border-b border-[#f1f5f9]">
              <span className="w-1.5 h-4 bg-[#056b4f] rounded-full"></span>
              <span className="text-[14px] font-bold text-[#0f172a]">身体素质项目</span>
            </div>

            {applicableItems.filter(it => it.category === 'fitness').map(it => {
              const val = studentScores[it.key]
              const res = checkStudentScorePass(selectedStudent, it, val, standardGrade)
              const isActive = activeItemKey === it.key

              return (
                <div
                  key={it.key}
                  onClick={() => handleOpenKeypad(it.key)}
                  className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                    isActive ? 'bg-[#f0fdf4] border-[#056b4f] ring-2 ring-[#056b4f]/20' : 'bg-[#f8fafc] border-[#e2e8f0]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] font-bold text-[#0f172a]">{it.label}</span>
                    <span className="text-[11px] text-[#94a3b8]">({it.unit})</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[16px] font-extrabold text-[#0f172a]">
                      {val !== undefined && val !== null && val !== '' ? String(val) : '--'}
                    </span>
                    {isActive && <span className="w-0.5 h-4 bg-[#056b4f] animate-pulse"></span>}
                    {res.tested && (
                      <span
                        className="px-2 py-0.5 rounded text-[11px] font-bold border"
                        style={{
                          backgroundColor: res.gradeBg,
                          borderColor: res.gradeBorder,
                          color: res.scoreColor,
                        }}
                      >
                        {res.scoreText} {res.gradeText}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* 固定底栏 (小键盘未激活时) */}
        {!showKeyboard && (
          <div className="fixed bottom-0 left-0 right-0 p-3.5 bg-white/95 backdrop-blur-md border-t border-[#e2e8f0] z-40 max-w-lg mx-auto flex items-center gap-2.5">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSaveStudentTest(false)}
              className="flex-1 h-12 rounded-full font-bold text-[14.5px] text-[#056b4f] bg-[#f0fdf4] border border-[#a7f3d0] active:bg-[#e8f6f0] transition-all flex items-center justify-center gap-1.5"
            >
              <span>仅保存当前成绩</span>
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={() => handleSaveStudentTest(true)}
              className="flex-1 h-12 rounded-full font-bold text-[14.5px] text-white bg-gradient-to-r from-[#056b4f] to-[#04523c] active:opacity-90 transition-all flex items-center justify-center gap-1.5 shadow-md"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>保存并进入下一位</span>
                  <span>▶</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* 专属数字小键盘 */}
        <SportScoreKeyboard
          visible={showKeyboard && !!activeItemKey}
          activeField={activeItemKey}
          fieldName={activeItemObj.label}
          unit={activeItemObj.unit}
          value={studentScores[activeItemKey] || ''}
          itemProgressText={currentItemIndex >= 0 ? `${currentItemIndex + 1}/${applicableItems.length}` : ''}
          onChangeValue={handleScoreChange}
          onPrevField={handlePrevField}
          onNextField={handleNextField}
          hasPrevField={currentItemIndex > 0}
          hasNextField={currentItemIndex < applicableItems.length - 1}
          studentInfo={{
            name: selectedStudent.real_name || selectedStudent.name,
            index: currentStudentIndex,
            total: studentCohortList.length,
          }}
          nextButtonText="下一项"
          prevButtonText="上一项"
          showStudentBar={false}
          showZeroButton={true}
          onClose={() => setShowKeyboard(false)}
        />
      </div>
    )
  }

  // ==================== 视图 1：单人速测速寻与班级列表态 ====================
  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] pb-24 font-sans select-none">
      {/* 顶栏导航 */}
      <div
        className="sticky top-0 z-30 px-4 pt-3 pb-3.5 shadow-sm"
        style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)' }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h1 className="text-[17.5px] font-bold text-white tracking-wide leading-tight">单人速测</h1>
            <p className="text-[11.5px] text-white/80 font-normal mt-0.5">精准单兵补测 · 全校速寻录入</p>
          </div>

          <div className="w-9 h-9" />
        </div>
      </div>

      <div className="px-3.5 pt-3 space-y-3 max-w-lg mx-auto">
        {/* 1. 快速检索输入框 (与 App 端一致，支持姓名首字母、学籍号) */}
        <div className="relative flex items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="输入拼音首字母(如 ZS) / 姓名 / 学号 / 班级"
            className="w-full pl-9 pr-9 py-2.5 rounded-2xl bg-white border border-[#e2e8f0] text-[13.5px] text-[#0f172a] placeholder-[#94a3b8] shadow-sm focus:outline-none focus:border-[#056b4f]"
          />
          <svg className="w-4 h-4 text-[#056b4f] absolute left-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 w-5 h-5 rounded-full bg-[#cbd5e1] text-white flex items-center justify-center text-[11px]"
            >
              ✕
            </button>
          )}
        </div>

        {/* 2. 当输入检索词时：展示实时匹配学生名册 */}
        {searchQuery.trim().length > 0 ? (
          <div className="space-y-2">
            <div className="text-[12px] text-[#64748b] px-1 font-semibold">
              检索到 {matchedStudents.length} 名匹配学生
            </div>

            {matchedStudents.map(stu => {
              const isMale = stu.gender === 'male' || stu.gender === '男'
              return (
                <div
                  key={stu.id}
                  onClick={() => handleSelectStudentForEntry(stu, matchedStudents)}
                  className="bg-white rounded-2xl p-3.5 shadow-sm border border-[#e2e8f0] active:border-[#056b4f] flex items-center justify-between cursor-pointer transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-extrabold text-[15px] ${
                        isMale ? 'bg-[#056b4f]' : 'bg-[#ec4899]'
                      }`}
                    >
                      {(stu.real_name || stu.name || '?').charAt(0)}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-bold text-[#0f172a]">
                          {stu.real_name || stu.name}
                        </span>
                        <span
                          className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                            isMale ? 'bg-[#e8f6f0] text-[#056b4f]' : 'bg-[#fce7f3] text-[#ec4899]'
                          }`}
                        >
                          {isMale ? '男' : '女'}
                        </span>
                      </div>

                      <div className="text-[11.5px] text-[#64748b] mt-0.5">
                        {stu.grade || ''} · {stu.class_name || '主校区'}
                        {stu.education_id ? ` · ID: ${stu.education_id}` : (stu.student_no ? ` · 学号: ${stu.student_no}` : '')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-[12.5px] font-bold text-[#056b4f] bg-[#f0fdf4] px-3 py-1.5 rounded-xl border border-[#a7f3d0]">
                    <span>速测录入</span>
                    <span>›</span>
                  </div>
                </div>
              )
            })}

            {matchedStudents.length === 0 && (
              <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
                未检索到与 "{searchQuery}" 相关的学生
              </div>
            )}
          </div>
        ) : (
          /* 3. 未检索时：展示年级筛选胶囊 + 班级名册列表 (完全对齐 App) */
          <div className="space-y-3">
            {/* 年级滑动过滤栏 */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              {GRADE_FILTER_OPTIONS.map(g => {
                const active = gradeFilter === g
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGradeFilter(g)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-all ${
                      active
                        ? 'bg-[#056b4f] text-white font-bold shadow-sm'
                        : 'bg-white text-[#64748b] border border-[#e2e8f0] active:bg-[#f1f5f9]'
                    }`}
                  >
                    {g}
                  </button>
                )
              })}
            </div>

            {/* 班级列表 */}
            <div className="space-y-2">
              <div className="text-[12px] text-[#64748b] px-1 font-semibold">
                选择班级进行单兵补测 (共 {filteredClasses.length} 个班级)
              </div>

              {loading ? (
                <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
                  正在加载班级名册...
                </div>
              ) : filteredClasses.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
                  暂无匹配的班级数据
                </div>
              ) : (
                filteredClasses.map(cls => (
                  <div
                    key={cls.id}
                    onClick={() => handleSelectClass(cls)}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0] active:border-[#056b4f] flex items-center justify-between cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-[#f0fdf4] border border-[#a7f3d0] flex items-center justify-center text-[#056b4f]">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[15px] font-bold text-[#0f172a]">
                            {cls.class_name}
                          </span>
                          {cls.grade && (
                            <span className="px-1.5 py-0.2 rounded text-[10.5px] bg-[#f1f5f9] text-[#64748b]">
                              {cls.grade}
                            </span>
                          )}
                        </div>

                        <div className="text-[11.5px] text-[#64748b] mt-0.5">
                          {cls.student_count !== undefined ? `${cls.student_count} 名学生` : '点击选择学生录入'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-[12.5px] font-bold text-[#056b4f]">
                      <span>查看名单</span>
                      <span>›</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
